import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useMap } from '../../context/MapContext';
import MetaModal from '../Modals/MetaModal';
import AttrModal from '../Modals/AttrModal';
import { fetchWmsLayerBbox } from './wmsBounds';
import './LayerCustomize.css';

/* ── Ícones ── */
function PencilIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9"/>
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
    </svg>
  );
}

function WmsIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3"   width="7" height="7"/>
      <rect x="14" y="3"  width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/>
      <rect x="3" y="14"  width="7" height="7"/>
    </svg>
  );
}

function DropIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function ZoomIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      <line x1="11" y1="8" x2="11" y2="14"/>
      <line x1="8" y1="11" x2="14" y2="11"/>
    </svg>
  );
}

function SpinnerIcon() {
  return <span className="lyc-spinner" aria-hidden="true" />;
}

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

/* ── Componente principal ── */
function LayerCustomize({ layer, onClose }) {
  const { setWmsOpacity, toggleWmsVisibility, requestZoom, updateLayerMetaCache } = useMap();
  const [showMeta,    setShowMeta]    = useState(false);
  const [showAttr,    setShowAttr]    = useState(false);
  const [urlCopied,   setUrlCopied]   = useState(false);
  const [zoomLoading, setZoomLoading] = useState(false);
  const [legendImg,   setLegendImg]   = useState(null);
  const [legendError, setLegendError] = useState(false);

  const opacityPct = Math.round(layer.opacity * 100);

  /* Busca URL da legenda */
  useEffect(() => {
    const legendUrl =
      `${layer.baseUrl}?service=WMS&request=GetLegendGraphic` +
      `&version=1.0.0&layer=${encodeURIComponent(layer.layers)}&format=image/png` +
      `&legend_options=fontAntiAliasing:true;fontSize:12;dpi:96`;
    setLegendImg(`/api/wms-proxy?url=${encodeURIComponent(legendUrl)}`);
    setLegendError(false);
  }, [layer.baseUrl, layer.layers]);

  /* Zoom para extensão da camada */
  const handleZoom = useCallback(async () => {
    if (layer.metaCache?.bbox) {
      const { bbox } = layer.metaCache;
      requestZoom([bbox.miny, bbox.minx, bbox.maxy, bbox.maxx]);
      return;
    }
    setZoomLoading(true);
    try {
      const bbox = await fetchWmsLayerBbox(layer);
      if (bbox) {
        updateLayerMetaCache(layer.id, { bbox });
        requestZoom([bbox.miny, bbox.minx, bbox.maxy, bbox.maxx]);
      }
    } catch (err) {
      console.warn('LayerCustomize: zoom failed', err);
    } finally {
      setZoomLoading(false);
    }
  }, [layer, requestZoom, updateLayerMetaCache]);

  /* Copiar URL GetMap */
  const handleCopyUrl = useCallback(() => {
    const url =
      `${layer.baseUrl}?service=WMS&request=GetMap` +
      `&layers=${layer.layers}&format=${layer.format}` +
      `&transparent=${layer.transparent}&version=${layer.version}` +
      `&srs=EPSG:4326&width=256&height=256&bbox=-180,-90,180,90`;
    navigator.clipboard.writeText(url).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2500);
  }, [layer]);

  /* Downloads WFS */
  function wfsUrl(format) {
    const base = layer.baseUrl.replace('/wms', '/wfs');
    return `${base}?service=WFS&request=GetFeature&typeName=${layer.layers}&outputFormat=${encodeURIComponent(format)}`;
  }

  function handleReset() {
    setWmsOpacity(layer.id, 1);
  }

  return (
    <div className="lyc">
      {/* ── Cabeçalho ── */}
      <div className="lyc-header">
        <span className="lyc-header-icon"><PencilIcon /></span>
        <span className="lyc-header-title">Personalização do Mapa</span>
        <button className="lyc-close" onClick={onClose} aria-label="Fechar personalização">×</button>
      </div>

      {/* ── Corpo ── */}
      <div className="lyc-body">

        {/* Identidade da camada */}
        <div className="lyc-identity">
          <div className="lyc-identity-icon"><WmsIcon /></div>
          <div className="lyc-identity-type">WMS</div>
          <div className="lyc-identity-name">{layer.name}</div>
        </div>

        {/* Visibilidade */}
        <div className="lyc-vis-row">
          <button
            className={`lyc-vis-btn${layer.visible ? ' lyc-vis-btn--on' : ''}`}
            onClick={() => toggleWmsVisibility(layer.id)}
            aria-pressed={layer.visible}
          >
            {layer.visible ? <EyeIcon /> : <EyeOffIcon />}
            <span>{layer.visible ? 'Visível no mapa' : 'Oculta no mapa'}</span>
          </button>
        </div>

        {/* Opacidade */}
        <div className="lyc-opacity">
          <div className="lyc-opacity-header">
            <span className="lyc-opacity-icon"><DropIcon /></span>
            <span className="lyc-opacity-label">Opacidade</span>
            <span className="lyc-opacity-val">{opacityPct}%</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={opacityPct}
            onChange={(e) => setWmsOpacity(layer.id, Number(e.target.value) / 100)}
            className="lyc-slider"
            aria-label={`Opacidade da camada ${layer.name}`}
            style={{ '--pct': `${opacityPct}%` }}
          />
        </div>

        {/* Legenda */}
        <div className="lyc-legend">
          <div className="lyc-section-title">LEGENDA</div>
          {legendImg && !legendError ? (
            <div className="lyc-legend-img-wrap">
              <img
                src={legendImg}
                alt={`Legenda da camada ${layer.name}`}
                className="lyc-legend-img"
                onError={() => setLegendError(true)}
              />
            </div>
          ) : (
            <p className="lyc-legend-empty">Legenda não disponível para esta camada.</p>
          )}
        </div>

        {/* Ferramentas */}
        <div className="lyc-section-title">FERRAMENTAS</div>
        <div className="lyc-tools">
          <button
            className="lyc-tool"
            onClick={handleZoom}
            disabled={zoomLoading}
            title="Centralizar o mapa na extensão da camada"
          >
            {zoomLoading ? <SpinnerIcon /> : <ZoomIcon />}
            <span>Zoom</span>
          </button>

          <button
            className="lyc-tool"
            onClick={() => setShowMeta(true)}
            title="Ver metadados WMS"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>Metadados</span>
          </button>

          <button
            className={`lyc-tool${urlCopied ? ' lyc-tool--success' : ''}`}
            onClick={handleCopyUrl}
            title="Copiar URL GetMap"
          >
            <CopyIcon />
            <span>{urlCopied ? 'Copiado!' : 'URL'}</span>
          </button>

          <button
            className="lyc-tool"
            onClick={() => setShowAttr(true)}
            title="Ver atributos (WFS)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="8" y1="6"  x2="21" y2="6"/>
              <line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6"  x2="3.01" y2="6"/>
              <line x1="3" y1="12" x2="3.01" y2="12"/>
              <line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
            <span>Atributos</span>
          </button>

          <button
            className="lyc-tool"
            onClick={() => window.open(wfsUrl('csv'), '_blank', 'noopener,noreferrer')}
            title="Baixar dados em CSV (WFS)"
          >
            <DownloadIcon />
            <span>CSV</span>
          </button>

          <button
            className="lyc-tool"
            onClick={() => window.open(wfsUrl('SHAPE-ZIP'), '_blank', 'noopener,noreferrer')}
            title="Baixar Shapefile (WFS)"
          >
            <DownloadIcon />
            <span>Shapefile</span>
          </button>
        </div>
      </div>

      {/* ── Rodapé ── */}
      <div className="lyc-footer">
        <button className="lyc-btn lyc-btn--reset" onClick={handleReset}>
          Resetar
        </button>
        <button className="lyc-btn lyc-btn--done" onClick={onClose}>
          Concluir
        </button>
      </div>

      {showMeta && <MetaModal layer={layer} onClose={() => setShowMeta(false)} />}
      {showAttr && <AttrModal layer={layer} onClose={() => setShowAttr(false)} />}
    </div>
  );
}

export default LayerCustomize;
